/**
 * useAppleDevice — Vue 3 composable for detecting Apple devices.
 * Identifies iPhone, iPad, Mac, and Vision Pro visitors to
 * promote the native App Store app where appropriate.
 */

import { ref, onMounted } from 'vue'

/**
 * @returns {{
 *   isAppleDevice: import('vue').Ref<boolean>,
 *   deviceName: import('vue').Ref<string>,
 *   appStoreUrl: string
 * }}
 */
export function useAppleDevice() {
  const isAppleDevice = ref(false)
  const deviceName = ref('')
  const appStoreUrl = 'https://apps.apple.com/app/apple-store/id6504160406?pt=126369107&ct=WordCloudStudo_Web&mt=8'

  function detect() {
    const ua = navigator.userAgent || ''
    const platform = navigator.platform || ''

    // Vision Pro (visionOS / xrOS)
    if (/xrOS|visionOS|VisionPro/i.test(ua) || /VisionPro/i.test(platform)) {
      isAppleDevice.value = true
      deviceName.value = 'Apple Vision Pro'
      return
    }

    // iPhone
    if (/iPhone/i.test(ua) || /iPhone/i.test(platform)) {
      isAppleDevice.value = true
      deviceName.value = 'iPhone'
      return
    }

    // iPad — traditional detection
    if (/iPad/i.test(ua) || /iPad/i.test(platform)) {
      isAppleDevice.value = true
      deviceName.value = 'iPad'
      return
    }

    // iPad — modern iPads with desktop-class browsing (iPadOS 13+)
    // They report as MacIntel but have touch support and no mobile phone capability
    if (
      /MacIntel/i.test(platform) &&
      navigator.maxTouchPoints > 1 &&
      !/iPhone|iPad|iPod/i.test(ua)
    ) {
      isAppleDevice.value = true
      deviceName.value = 'iPad'
      return
    }

    // Mac
    if (/Mac/i.test(ua) || /Mac/i.test(platform)) {
      isAppleDevice.value = true
      deviceName.value = 'Mac'
      return
    }
  }

  onMounted(() => {
    detect()
  })

  return { isAppleDevice, deviceName, appStoreUrl }
}

export default useAppleDevice
